/**
 * FigmaConnectModal — paste-token (personal access token) form for the Figma
 * connector.
 *
 * Figma auth is now a PERSONAL ACCESS TOKEN (PAT), not OAuth. Same single-field
 * shape as LinearConnectModal: the user pastes ONE value:
 *   - Personal access token (from Figma → Settings → "Personal access tokens";
 *     format `figd_...`)
 *
 * Backend validates the token (GET /v1/me with X-Figma-Token) before
 * persisting — structured error surfaces on err.message. Mirrors
 * LinearConnectModal exactly; just Figma copy + link.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectFigma } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function FigmaConnectModal({ isOpen, onClose, onConnected }) {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectFigma();

  useEffect(() => {
    if (isOpen) {
      setApiKey('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!apiKey.trim()) {
      setError('Personal access token is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { apiKey: apiKey.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Figma. Please check your token.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Figma"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Figma</h2>
        <p className="lark-modal-description">
          Create a personal access token in{' '}
          <a
            href="https://www.figma.com/settings"
            target="_blank"
            rel="noopener noreferrer"
          >
            Figma → Settings → Personal access tokens
          </a>
          , then paste it below. Workflows can then read your Figma files,
          frames, and comments and render designs.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="figma-api-key">Personal Access Token</label>
          <input
            id="figma-api-key"
            type="password"
            value={apiKey}
            placeholder="figd_..."
            onChange={(e) => { setApiKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !apiKey.trim()}
            aria-label="Submit Figma credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Figma connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
